
import React, { useEffect, useState } from "react";
import {
  FiSave,
  FiRefreshCw,
  FiSettings,
  FiInfo,
  FiPlus,
  FiTrash2,
} from "react-icons/fi";
import api from "../../utills/api";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { Card, CardHeader, Col, Row } from "reactstrap";

const emptyOrganization = {
  name: "",
  type: "College",
  academicYear: "",
  timezone: "Asia/Kolkata",
  courses: [],
};

const Organization = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [organization, setOrganization] = useState(emptyOrganization);

  const [courseName, setCourseName] = useState("");
  const [courseStreams, setCourseStreams] = useState("");
  const [editingIndex, setEditingIndex] = useState(-1);

  const normalizeCourses = (courses) =>
    Array.isArray(courses)
      ? courses.map((course) => ({
          name: String(course.name || "").trim(),
          streams: Array.isArray(course.streams)
            ? course.streams
                .map((stream) => String(stream).trim())
                .filter(Boolean)
            : [],
        }))
      : [];

  const loadOrganization = async () => {
    try {
      setLoading(true);
      setMessage("");

      const response = await api.get("/organization");
      const data = response?.data?.data || response?.data || {};

      setOrganization({
        ...emptyOrganization,
        ...data,
        name: data.name || "",
        type: data.type || "College",
        academicYear: data.academicYear || "",
        timezone: data.timezone || "Asia/Kolkata",
        courses: normalizeCourses(data.courses),
      });
    } catch (error) {
      console.error("Failed to load organization:", error);
      setMessageType("danger");
      setMessage(
        error?.response?.data?.message ||
          "Unable to load organization details."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrganization();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setOrganization((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetCourseForm = () => {
    setCourseName("");
    setCourseStreams("");
    setEditingIndex(-1);
  };

  // Save course/stream changes to MongoDB through the organization API.
  const persistCourses = async (updatedCourses, successMessage) => {
    try {
      setSaving(true);
      setMessage("");

      const payload = {
        ...organization,
        courses: normalizeCourses(updatedCourses),
      };

      const response = await api.put("/organization", payload);

      if (response?.data?.success === false) {
        throw new Error(
          response?.data?.message || "The server could not save courses."
        );
      }

      const savedOrganization = response?.data?.data;

      // Prefer the courses returned by the backend.
      // If the endpoint doesn't return the organization, use the saved payload.
      const savedCourses = savedOrganization
        ? normalizeCourses(savedOrganization.courses)
        : payload.courses;

      setOrganization((prev) => ({
        ...prev,
        ...(savedOrganization || {}),
        courses: savedCourses,
      }));

      setMessageType("success");
      setMessage(successMessage);
      return true;
    } catch (error) {
      console.error(
        "Failed to save courses/streams:",
        error?.response?.data || error
      );

      setMessageType("danger");
      setMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to save courses and streams. Please try again."
      );

      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleAddCourse = async (event) => {
    event.preventDefault();
    setMessage("");

    const name = courseName.trim();

    if (!name) {
      setMessageType("warning");
      setMessage("Please enter a course name.");
      return;
    }

    const duplicate = organization.courses.some(
      (course, index) =>
        index !== editingIndex &&
        course.name.toLowerCase() === name.toLowerCase()
    );

    if (duplicate) {
      setMessageType("warning");
      setMessage("This course already exists.");
      return;
    }

    const streams = [
      ...new Set(
        courseStreams
          .split(",")
          .map((stream) => stream.trim())
          .filter(Boolean)
      ),
    ];

    const updatedCourses = [...organization.courses];
    const course = { name, streams };

    if (editingIndex >= 0) {
      updatedCourses[editingIndex] = course;
    } else {
      updatedCourses.push(course);
    }

    const saved = await persistCourses(
      updatedCourses,
      editingIndex >= 0
        ? "Course and streams updated successfully."
        : "Course and streams added successfully."
    );

    if (saved) {
      resetCourseForm();
    }
  };

  const handleEditCourse = (index) => {
    const course = organization.courses[index];

    setCourseName(course.name);
    setCourseStreams(course.streams.join(", "));
    setEditingIndex(index);
    setMessage("");
  };

  const handleRemoveCourse = async (index) => {
    const course = organization.courses[index];

    if (!window.confirm(`Remove "${course.name}" from this list?`)) {
      return;
    }

    const updatedCourses = organization.courses.filter(
      (_, courseIndex) => courseIndex !== index
    );

    const saved = await persistCourses(
      updatedCourses,
      "Course removed successfully."
    );

    if (!saved) return;

    if (editingIndex === index) {
      resetCourseForm();
    } else if (editingIndex > index) {
      setEditingIndex((prev) => prev - 1);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      setMessage("");

      const payload = {
        ...organization,
        courses: normalizeCourses(organization.courses),
      };

      const response = await api.put("/organization", payload);

      if (response?.data?.success === false) {
        throw new Error(
          response?.data?.message || "Unable to save organization settings."
        );
      }

      const saved = response?.data?.data;

      if (saved) {
        setOrganization((prev) => ({
          ...prev,
          ...saved,
          courses: normalizeCourses(
            Array.isArray(saved.courses)
              ? saved.courses
              : payload.courses
          ),
        }));
      }

      setMessageType("success");
      setMessage("Organization settings saved successfully.");
    } catch (error) {
      console.error(
        "Failed to save organization:",
        error?.response?.data || error
      );

      setMessageType("danger");
      setMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to save organization settings."
      );
    } finally {
      setSaving(false);
    }
  };

  document.title = "Organisation | eduLeads.Online";

  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb title="Organisation" pageTitle="Settings" />

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader>
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <div className="flex-grow-1">
                    <h5 className="mb-0">Organization</h5>
                    <small className="text-muted">
                      Manage your education organization settings.
                    </small>
                  </div>

                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={loadOrganization}
                    disabled={loading || saving}
                  >
                    <FiRefreshCw className="me-1" size={16} />
                    Refresh
                  </button>
                </div>
              </CardHeader>
            </Card>
          </Col>
        </Row>

        {message && (
          <div className={`alert alert-${messageType}`}>
            <FiInfo size={16} className="me-2" />
            {message}
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            Loading organization...
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">
                  <FiSettings size={17} className="me-2" />
                  Organization Details
                </h5>
              </CardHeader>

              <div className="card-body">
                <Row>
                  <Col lg={6} className="mb-3">
                    <label className="form-label">
                      Organization Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      className="form-control"
                      value={organization.name}
                      onChange={handleChange}
                      required
                    />
                  </Col>

                  <Col lg={6} className="mb-3">
                    <label className="form-label">
                      Organization Type
                    </label>
                    <select
                      name="type"
                      className="form-select"
                      value={organization.type}
                      onChange={handleChange}
                    >
                      <option value="School">School</option>
                      <option value="College">College</option>
                      <option value="University">University</option>
                      <option value="Coaching Institute">
                        Coaching Institute
                      </option>
                      <option value="Other">Other</option>
                    </select>
                  </Col>

                  <Col lg={6} className="mb-3">
                    <label className="form-label">
                      Academic Year
                    </label>
                    <input
                      type="text"
                      name="academicYear"
                      className="form-control"
                      placeholder="2026-27"
                      value={organization.academicYear}
                      onChange={handleChange}
                    />
                  </Col>

                  <Col lg={6} className="mb-3">
                    <label className="form-label">Timezone</label>
                    <select
                      name="timezone"
                      className="form-select"
                      value={organization.timezone}
                      onChange={handleChange}
                    >
                      <option value="Asia/Kolkata">
                        Asia/Kolkata
                      </option>
                      <option value="UTC">UTC</option>
                    </select>
                  </Col>
                </Row>
              </div>
            </Card>

            <Card>
              <CardHeader>
                <h5 className="card-title mb-1">
                  Courses &amp; Streams
                </h5>
                <small className="text-muted">
                  Configure the courses offered by your organization
                  and the streams available under each course.
                </small>
              </CardHeader>

              <div className="card-body">
                <Row className="align-items-end">
                  <Col lg={4} className="mb-3">
                    <label className="form-label">Course Name</label>
                    <input
                      className="form-control"
                      value={courseName}
                      onChange={(event) =>
                        setCourseName(event.target.value)
                      }
                      placeholder="e.g. B.Tech"
                      disabled={saving}
                    />
                  </Col>

                  <Col lg={5} className="mb-3">
                    <label className="form-label">Streams</label>
                    <input
                      className="form-control"
                      value={courseStreams}
                      onChange={(event) =>
                        setCourseStreams(event.target.value)
                      }
                      placeholder="Computer Science, Civil, Mechanical"
                      disabled={saving}
                    />
                    <small className="text-muted">
                      Separate each stream with a comma.
                    </small>
                  </Col>

                  <Col lg={3} className="mb-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary me-2"
                      onClick={resetCourseForm}
                      disabled={saving}
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleAddCourse}
                      disabled={saving}
                    >
                      <FiPlus className="me-1" />
                      {saving
                        ? "Saving..."
                        : editingIndex >= 0
                        ? "Update"
                        : "Add"}
                    </button>
                  </Col>
                </Row>

                <div className="table-responsive mt-3">
                  <table className="table table-bordered table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "30%" }}>Course</th>
                        <th>Streams</th>
                        <th style={{ width: "150px" }}>Actions</th>
                      </tr>
                    </thead>

                    <tbody>
                      {organization.courses.length === 0 ? (
                        <tr>
                          <td
                            colSpan="3"
                            className="text-center text-muted py-4"
                          >
                            No courses added yet.
                          </td>
                        </tr>
                      ) : (
                        organization.courses.map((course, index) => (
                          <tr key={`${course.name}-${index}`}>
                            <td className="fw-semibold">
                              {course.name}
                            </td>
                            <td>
                              {course.streams.length > 0
                                ? course.streams.join(", ")
                                : "No streams configured"}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary me-2"
                                onClick={() => handleEditCourse(index)}
                                disabled={saving}
                              >
                                Edit
                              </button>

                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleRemoveCourse(index)}
                                title="Remove course"
                                disabled={saving}
                              >
                                <FiTrash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </Card>

            <div className="d-flex justify-content-end mt-3 mb-4">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                <FiSave size={15} className="me-1" />
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Organization;
